// Shared UI primitives: Logo, Toast, Modal, ConfirmDialog, badges, sparkline,
// resource bar, stepper, empty state, service icon helper, icon button, avatar.

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
  type ReactNode,
  type ReactElement,
} from 'react';
import { I } from './icons';

// ─── Logo ────────────────────────────────────────────────────────────────────
export function Logo({ size = 26, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" className={'brand-mark ' + className}
         xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="brandG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--accent-hi)" />
          <stop offset="1" stopColor="var(--accent-lo)" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="32" height="32" rx="8" fill="url(#brandG)" />
      <path d="M11 9.5 H21 V13 H13.5 V14.8 H21 V22.5 H11 V19 H18.5 V17.2 H11 Z"
            fill="var(--accent-fg)" opacity="0.96"/>
      <circle cx="23" cy="9.6" r="1.6" fill="var(--accent-fg)" opacity="0.6"/>
    </svg>
  );
}

// ─── Toasts ─────────────────────────────────────────────────────────────────
type ToastKind = 'info' | 'success' | 'warn' | 'error';
type Toast = { id: string; kind: ToastKind; title: string; msg?: string; duration?: number };
type ToastApi = {
  info: (title: string, msg?: string) => void;
  success: (title: string, msg?: string) => void;
  warn: (title: string, msg?: string) => void;
  error: (title: string, msg?: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: string) => setToasts((ts) => ts.filter((x) => x.id !== id)), []);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((ts) => [...ts, { id, kind: 'info', ...t }]);
    // Errors stay until dismissed — a backend message shouldn't vanish before
    // it can be read/copied. Everything else auto-dismisses.
    if (t.kind !== 'error') {
      setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), t.duration || 4200);
    }
  }, []);
  const api = useMemo<ToastApi>(() => ({
    info: (title, msg) => push({ kind: 'info', title, msg }),
    success: (title, msg) => push({ kind: 'success', title, msg }),
    warn: (title, msg) => push({ kind: 'warn', title, msg }),
    error: (title, msg) => push({ kind: 'error', title, msg }),
  }), [push]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-zone">
        {toasts.map((t) => (
          <div key={t.id} className="toast">
            <div className="toast-icon" style={{ color: ({ success: 'var(--green)', warn: 'var(--amber)', error: 'var(--red)', info: 'var(--accent)' } as Record<ToastKind, string>)[t.kind] }}>
              {t.kind === 'success' ? <I.CheckCirc size={18}/> :
               t.kind === 'warn'    ? <I.Warn size={18}/> :
               t.kind === 'error'   ? <I.XCirc size={18}/> :
                                      <I.Info size={18}/>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="toast-title">{t.title}</div>
              {t.msg && <div className="toast-msg" style={{ userSelect: 'text' }}>{t.msg}</div>}
            </div>
            <button className="btn btn-ghost btn-icon btn-sm" title="Dismiss" aria-label="Dismiss notification"
                    onClick={() => dismiss(t.id)} style={{ flex: 'none', marginTop: -2 }}>
              <I.X size={13}/>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi | null {
  return useContext(ToastContext);
}

// ─── Modal ──────────────────────────────────────────────────────────────────
export function Modal({
  open, onClose, title, subtitle, children, footer, size = 'md', maxWidth, dismissable = true,
}: {
  open: boolean;
  onClose?: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Exact width in px when the four sizes don't fit — a dialog whose content
   *  has a natural width that lands between two of them. Overrides `size`. */
  maxWidth?: number;
  dismissable?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && dismissable) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, dismissable]);
  if (!open) return null;
  const sizeCls = size === 'sm' ? 'modal-sm' : size === 'lg' ? 'modal-lg' : size === 'xl' ? 'modal-xl' : '';
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && dismissable) onClose?.(); }}>
      <div className={'modal ' + sizeCls} style={maxWidth ? { maxWidth } : undefined}>
        {title && (
          <div className="modal-head">
            <div>
              <h2>{title}</h2>
              {subtitle && <p>{subtitle}</p>}
            </div>
            {dismissable && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
                <I.X size={15}/>
              </button>
            )}
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// ─── Confirm ────────────────────────────────────────────────────────────────
export function ConfirmDialog({
  open, title, message, confirmLabel = 'Confirm', danger, onConfirm, onCancel,
}: {
  open: boolean;
  title?: ReactNode;
  message?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}) {
  return (
    <Modal open={open} onClose={onCancel} title={title} subtitle={message} dismissable={true}
           footer={
             <>
               <button className="btn" onClick={onCancel}>Cancel</button>
               <button className={'btn ' + (danger ? 'btn-danger-solid' : 'btn-primary')} onClick={onConfirm}>{confirmLabel}</button>
             </>
           }>
      <div style={{ padding: 0 }}/>
    </Modal>
  );
}

// ─── Status Badge ───────────────────────────────────────────────────────────
//
// Accepts both FE-internal names (healthy/pending/...) and the raw BE enum
// the API returns (ready/provisioning/suspended). The latter are aliased
// onto the same visual buckets so we don't surface "Unknown" for valid states.
export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { cls: string; label: string; live?: boolean }> = {
    healthy:      { cls: 'badge-green',  label: 'Healthy' },
    ready:        { cls: 'badge-green',  label: 'Healthy' },
    degraded:     { cls: 'badge-amber',  label: 'Degraded' },
    failing:      { cls: 'badge-red',    label: 'Failing' },
    pending:      { cls: 'badge-blue',   label: 'Pending',      live: true },
    provisioning: { cls: 'badge-blue',   label: 'Provisioning', live: true },
    syncing:      { cls: 'badge-blue',   label: 'Syncing',      live: true },
    restarting:   { cls: 'badge-amber',  label: 'Restarting…',  live: true },
    starting:     { cls: 'badge-blue',   label: 'Starting…',    live: true },
    suspended:    { cls: '',             label: 'Suspended' },
    unknown:      { cls: '',             label: 'Unknown' },
  };
  const m = map[status] || map.unknown;
  // Pick the dot class that the CSS already styles; falls back to the badge
  // key itself (we add CSS rules for the new aliases below).
  return (
    <span className={'badge ' + m.cls + (m.live ? ' badge-live' : '')}>
      <span className={'status-dot ' + status} style={{ boxShadow: 'none' }}></span>
      {m.label}
    </span>
  );
}

export function StatusDot({ status }: { status: string }) {
  return <span className={'status-dot ' + status}></span>;
}

// ─── Sparkline ──────────────────────────────────────────────────────────────
export function Sparkline({
  data, width = 200, height = 28, color = 'var(--accent)',
}: { data: number[]; width?: number; height?: number; color?: string }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = (max - min) || 1;
  const step = width / (data.length - 1 || 1);
  const points = data.map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  const area = `M0,${height} L${points} L${width},${height} Z`;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <path d={area} fill={color} opacity="0.12"/>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── Resource bar ───────────────────────────────────────────────────────────
export function ResourceBar({
  value, max = 100, label, warnAt = 70, critAt = 90,
}: { value: number; max?: number; label?: string; warnAt?: number; critAt?: number }) {
  // value < 0 is the "n/a" sentinel (no metrics from k8s) — render a muted
  // dash + empty track instead of a negative-width bar.
  const na = value < 0;
  const pct = na ? 0 : Math.min(100, (value / max) * 100);
  const cls = pct >= critAt ? 'crit' : pct >= warnAt ? 'warn' : '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <div className="spread" style={{ fontSize: 11.5 }}>
          <span className="muted">{label}</span>
          <span className="mono" style={{ color: na ? 'var(--text-3)' : 'var(--text-1)' }}>{na ? 'n/a' : Math.round(pct) + '%'}</span>
        </div>
      )}
      <div className="bar"><div className={'bar-fill ' + cls} style={{ width: pct + '%' }}/></div>
    </div>
  );
}

// ─── Stepper ────────────────────────────────────────────────────────────────
export function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="stepper">
      {steps.map((label, i) => (
        <span key={i} style={{ display: 'contents' }}>
          <div className={'step ' + (i < current ? 'done' : '') + (i === current ? ' active' : '')}>
            <div className="num">{i < current ? <I.Check size={13}/> : i + 1}</div>
            <span>{label}</span>
          </div>
          {i < steps.length - 1 && <div className={'step-line ' + (i < current ? 'done' : '')}/>}
        </span>
      ))}
    </div>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────
export function Empty({
  icon, title, message, action,
}: { icon?: ReactNode; title: ReactNode; message?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon && <div className="empty-icon">{icon}</div>}
      <div style={{ fontWeight: 500, color: 'var(--text-1)' }}>{title}</div>
      {message && <div style={{ marginTop: 6 }}>{message}</div>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

// ─── Service icon helper ────────────────────────────────────────────────────
export function ServiceIcon({ name, size = 14 }: { name: string; size?: number }): ReactElement {
  const Comp = I[name] || I.Cube;
  return <Comp size={size}/>;
}

// ─── Topbar action button ───────────────────────────────────────────────────
export function IconBtn({
  onClick, title, children, active, disabled,
}: { onClick?: () => void; title?: string; children?: ReactNode; active?: boolean; disabled?: boolean }) {
  return (
    <button className={'btn btn-ghost btn-icon btn-sm' + (active ? ' is-active' : '')}
            onClick={onClick} title={title} disabled={disabled}>
      {children}
    </button>
  );
}

// ─── Avatar from name ───────────────────────────────────────────────────────
export function Avatar({ name, size = 28 }: { name?: string; size?: number }) {
  const initials = (name || '?').split(/[\s-_]/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');
  return <div className="avatar" style={{ width: size, height: size, fontSize: size * 0.42 }}>{initials}</div>;
}

// ─── Key/value cells (used in both client and global detail views) ──────────
export function KV({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div>
      <div className="metric-label" style={{ marginBottom: 6 }}>{label}</div>
      <div>{value}</div>
    </div>
  );
}

export function KVrow({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="spread">
      <span className="muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}
